/**
 * Facebook Pixel Event Tracking Utilities
 *
 * Active Meta Pixel ID: 994550223073937 (ULC World production dataset).
 * The pixel itself is initialized in index.html. Do NOT reference the
 * no-longer-used dataset 682316655848538 anywhere in this codebase.
 *
 * Helper functions for tracking standard and custom Facebook Pixel events.
 * Includes a small queue so events fired before fbq finishes loading are not lost.
 *
 * REMOVAL INSTRUCTIONS:
 * 1. Delete this file (src/lib/fbPixel.ts)
 * 2. Search for "trackFB" in the codebase and remove all imports and usages
 */

/** Active Meta Pixel ID — must match index.html. */
export const META_PIXEL_ID = '994550223073937';

type QueuedEvent = {
  type: 'track' | 'trackCustom';
  eventName: string;
  params?: Record<string, unknown>;
  eventID?: string;
};

/** Generate a unique event ID used to deduplicate browser + server (CAPI) events. */
export const newEventId = (): string => {
  try {
    if (typeof crypto !== 'undefined' && 'randomUUID' in crypto) return crypto.randomUUID();
  } catch {
    /* ignore */
  }
  return `evt-${Date.now()}-${Math.random().toString(36).slice(2, 11)}`;
};

const MAX_WAIT_MS = 8000;
const POLL_INTERVAL_MS = 200;
const queue: QueuedEvent[] = [];
let flushScheduled = false;

const isDev = typeof import.meta !== 'undefined' && (import.meta as { env?: { DEV?: boolean } }).env?.DEV;

// Verbose logging only in dev, or on a page opened with ?meta_test_code=... for Meta test-event runs.
const isTestMode =
  typeof window !== 'undefined' &&
  new URLSearchParams(window.location.search).has('meta_test_code');
const verbose = !!isDev || isTestMode;

if (verbose && typeof window !== 'undefined') {
  // eslint-disable-next-line no-console
  console.info('[fbPixel] Active Meta Pixel ID:', META_PIXEL_ID);
}



/** Routes where no Meta events may ever fire (private / account areas). */
const BLOCKED_ROUTE = /^\/(login|dashboard|admin)(\/|$)/i;
export const isMetaBlockedRoute = (path?: string): boolean =>
  typeof window !== 'undefined' && BLOCKED_ROUTE.test(path ?? window.location.pathname);

/** Only these parameter keys may reach Meta — no free text, no personal data. */
const ALLOWED_PARAMS = new Set(['content_name', 'content_category', 'content_ids', 'content_type', 'value', 'currency', 'action', 'search_string']);
const sanitizeParams = (params?: Record<string, unknown>) => {
  if (!params) return undefined;
  const out: Record<string, unknown> = {};
  for (const [k, v] of Object.entries(params)) if (ALLOWED_PARAMS.has(k)) out[k] = v;
  return out;
};

const fbqReady = (): boolean =>
  typeof window !== 'undefined' && typeof window.fbq === 'function';

const sendNow = (evt: QueuedEvent) => {
  try {
    window.fbq(evt.type, evt.eventName, evt.params, evt.eventID ? { eventID: evt.eventID } : undefined);
    if (verbose) {
      // eslint-disable-next-line no-console
      console.debug('[fbPixel] sent', evt.type, evt.eventName, evt.eventID ?? '(no eventID)', evt.params ?? {});
    }
  } catch (err) {
    // eslint-disable-next-line no-console
    verbose && console.warn('[fbPixel] failed to send event', evt.eventName, err);
  }
};

const scheduleFlush = () => {
  if (flushScheduled || typeof window === 'undefined') return;
  flushScheduled = true;
  const start = Date.now();
  const tick = () => {
    if (fbqReady()) {
      while (queue.length) {
        const evt = queue.shift();
        if (evt && !isMetaBlockedRoute()) sendNow(evt);
      }
      flushScheduled = false;
      return;
    }
    if (Date.now() - start > MAX_WAIT_MS) {
      // Drop quietly after timeout (likely blocked by ad blocker)
      if (verbose) {
        // eslint-disable-next-line no-console
        console.debug('[fbPixel] fbq never loaded; dropping', queue.length, 'queued events');
      }
      queue.length = 0;
      flushScheduled = false;
      return;
    }
    setTimeout(tick, POLL_INTERVAL_MS);
  };
  setTimeout(tick, POLL_INTERVAL_MS);
};

const enqueueOrSend = (evt: QueuedEvent) => {
  if (typeof window === 'undefined') return;
  if (isMetaBlockedRoute()) return;
  evt = { ...evt, params: sanitizeParams(evt.params) };
  if (fbqReady()) {
    sendNow(evt);
    return;
  }
  queue.push(evt);
  if (verbose) {
    // eslint-disable-next-line no-console
    console.debug('[fbPixel] queued (fbq not ready)', evt.eventName);
  }
  scheduleFlush();
};

/**
 * Track a standard Facebook Pixel event.
 * Returns the eventID so the same event can be sent server-side (CAPI) for deduplication.
 */
export const trackFBEvent = (
  eventName: string,
  params?: Record<string, unknown>,
  eventID: string = newEventId(),
): string => {
  enqueueOrSend({ type: 'track', eventName, params, eventID });
  return eventID;
};

/**
 * Track a custom Facebook Pixel event
 */
export const trackFBCustomEvent = (
  eventName: string,
  params?: Record<string, unknown>,
  eventID: string = newEventId(),
): string => {
  enqueueOrSend({ type: 'trackCustom', eventName, params, eventID });
  return eventID;
};
